// Geometry, tree shapes and the growth schedule of the homepage hero's
// landscape (HeroLandscape.vue): the far mountains, a misty conifer forest
// in four rows in front of them, and the meadow.
//
// Everything here is pure: the scene is a function of fixed seeds, of the
// visible width and of the time, so it can be rebuilt at any size without
// anything jumping, and any moment can be rendered exactly (renderAt).
//
// Units: the landscape band is 100 units tall and x is measured from the
// centre of the hero in the same units, so nothing is ever stretched — a
// wider screen simply reveals more mountains and more forest on both sides,
// and a phone shows a deliberate close-up of the centre.
//
// The forest is a port of the art lead's tree lab (the reference page
// __hero-lab.html): the spire() shape, its three KINDS, the per-row colours
// and the mist at the foot of each row come from there unchanged. The lab
// draws a 230 px tall scene, so its pixels become units through LAB.

import { seededRandom, clamp, lerp, ease } from './heroMotion';

/* ---------------------------------------------------------------------- */
/* Noise                                                                  */
/* ---------------------------------------------------------------------- */

// Integer hash -> 0..1 (murmur3 finaliser); works for negative indices.
export function hash(i, seed) {
  let h = Math.imul((i | 0) ^ 0x5bd1e995, 0x2c1b3c6d) ^ Math.imul(seed | 0, 0x297a2d39);
  h ^= h >>> 15;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// Smooth 1D value noise, 0..1.
export function noise(x, seed) {
  const i = Math.floor(x);
  const f = x - i;
  const s = f * f * f * (f * (f * 6 - 15) + 10);
  return lerp(hash(i, seed), hash(i + 1, seed), s);
}

function fbm(x, seed, octaves) {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let freq = 1;
  for (let o = 0; o < octaves; o++) {
    sum += noise(x * freq + o * 17.31, seed + o * 101) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2.03;
  }
  return sum / norm;
}

export function smoothstep(a, b, x) {
  const t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}

// Compact numbers for path data: two decimals for the hills and the near
// trees, one for the hundreds of small distant ones (a tenth of a unit is a
// fraction of a pixel at any size).
function n(v) {
  return Math.round(v * 100) / 100;
}

function n1(v) {
  return Math.round(v * 10) / 10;
}

/* ---------------------------------------------------------------------- */
/* Terrain                                                                */
/* ---------------------------------------------------------------------- */

// A mountain range: smooth peaks blended with a soft maximum (rounded
// valleys, no cusps) plus a little detail noise along the ridge. The peaks
// near the centre are lower so the range frames the hero text instead of
// fighting it.
function peaks(x, c) {
  const k0 = Math.floor(x / c.spacing);
  let acc = 0;
  for (let k = k0 - 3; k <= k0 + 3; k++) {
    const cx = (k + 0.5 + (hash(k, c.seed) - 0.5) * c.jitter) * c.spacing;
    const w = lerp(c.wMin, c.wMax, hash(k, c.seed + 1));
    const dip = lerp(c.dip, 1, smoothstep(25, 160, Math.abs(cx)));
    const h = lerp(c.hMin, c.hMax, hash(k, c.seed + 2)) * dip;
    const v = h / (1 + Math.pow(Math.abs(x - cx) / w, c.sharp));
    acc += Math.pow(v, 6);
  }
  const height = Math.pow(acc, 1 / 6) + (fbm(x / c.detailScale, c.seed + 5, 3) - 0.5) * c.detail;
  const y = c.base - height;
  // A soft ceiling (smooth, no kink): the tallest summits approach y = 4
  // but never touch the top of the band.
  return y < 10 ? 10 - 6 * (1 - Math.exp(-(10 - y) / 6)) : y;
}

// Irregular spacing, widths and heights, so the ranges never read as a
// row of repeated triangles.
const FAR_BACK = {
  seed: 101, spacing: 58, jitter: 0.7, wMin: 24, wMax: 44, hMin: 52, hMax: 76,
  base: 78, sharp: 1.75, dip: 0.88, detail: 3.6, detailScale: 9
};
const FAR_FRONT = {
  seed: 211, spacing: 44, jitter: 0.75, wMin: 16, wMax: 32, hMin: 40, hMax: 58,
  base: 77, sharp: 1.6, dip: 0.92, detail: 2.8, detailScale: 7
};

// Lab pixels -> units (the lab's scene is 230 px tall, the band 100 units).
const LAB = 100 / 230;

// The lab's four forest rows, back to front, in lab pixels: tree heights
// [min, range], spacing between trees [min, range], how deep their feet
// sink below the row's ground, and the row's ground line (where it lies in
// the lab's scene, and its gentle undulation). The two nearest rows have a
// lit sliver; the front row stands in small clusters.
const LAB_ROWS = [
  { h: [14, 16], gap: [3, 4], sink: 4, at: 128, amp: 8, freq: 62, seed: 3 },
  { h: [20, 22], gap: [3.6, 5], sink: 5, at: 146, amp: 9, freq: 50, seed: 5 },
  { h: [26, 30], gap: [4.4, 6], sink: 6, at: 164, amp: 8, freq: 44, seed: 9, near: true },
  { h: [34, 36], gap: [5.2, 7], sink: 8, at: 184, amp: 5, freq: 38, seed: 13, near: true, clusters: true }
];
// Where the lab's meadow begins, and the extra gap after each front-row
// cluster [min, range] (lab pixels).
const LAB_MEADOW = 198;
const LAB_CLUSTER_GAP = [4, 12];
// The lab's mist band in front of the far mountains, relative to the back
// row's ground (lab pixels), and how strong it is (a share of the back
// row's mist).
const LAB_MIST = [-58, 2];
const LAB_MIST_SHARE = 0.45;
// The bottom of every row's mist band, below the row's ground (lab pixels).
const LAB_HAZE_FOOT = 4;

// The forest's colours, straight from the lab: every row is filled with a
// vertical gradient (each tree from its tip to its foot) and veiled at its
// foot by a band of mist (haze: the band's opacity at the ground). The flat
// colours (each row's ground and the lit sliver of the two nearest rows)
// are in HeroLandscape.vue's styles.
export const FOREST_TONES = {
  day: {
    haze: '#e6f2ec',
    rows: [
      { top: '#93c7a0', bottom: '#7cb68b', haze: 0.3 },
      { top: '#63aa70', bottom: '#4b9259', haze: 0.18 },
      { top: '#3f9653', bottom: '#2a7a40', haze: 0.08 },
      { top: '#2d8645', bottom: '#1c612f', haze: 0 }
    ]
  },
  night: {
    haze: '#24443f',
    rows: [
      { top: '#2f6150', bottom: '#22493b', haze: 0.32 },
      { top: '#24573b', bottom: '#19402b', haze: 0.2 },
      { top: '#1d4d30', bottom: '#123520', haze: 0.1 },
      { top: '#18452a', bottom: '#0d2c18', haze: 0 }
    ]
  }
};

// The forest's rows in units, with what the app adds: `free` is the share
// of spots that can start empty and fill one tree at a time afterwards (the
// clustered front row has the edges of its clusters instead, about a third,
// see walk; planGrowth decides which of them the entrance really leaves
// empty), `grow` the s a tree of that later growth takes [min, max],
// `delay` the row's offset in the entrance wave, `salt` its random streams.
// The back row is fully grown by the entrance.
const APP_ROWS = [
  { free: 0, grow: [10, 12], delay: 0, salt: 1103 },
  { free: 1 / 3, grow: [10, 13], delay: 0.04, salt: 2207 },
  { free: 1 / 3, grow: [11, 14], delay: 0.08, salt: 3301 },
  { free: 0.3, grow: [13, 16], delay: 0.12, salt: 4409 }
];

export const ROWS = LAB_ROWS.map((o, i) => Object.assign({
  index: i,
  hMin: o.h[0] * LAB,
  hRange: o.h[1] * LAB,
  gapMin: o.gap[0] * LAB,
  gapRange: o.gap[1] * LAB,
  sink: o.sink * LAB,
  // The lab's mist band reaches up to 0.9 x the row's shortest tree.
  hazeDepth: o.h[0] * 0.9 * LAB,
  amp: o.amp * LAB,
  freq: o.freq * LAB,
  // The undulation's phase, as the lab draws it.
  phase: seededRandom(o.seed)() * 6,
  near: !!o.near,
  clusters: !!o.clusters
}, APP_ROWS[i]));

// The ground lines of the four rows for a given meadow line. In the lab the
// rows sit at fixed depths above the meadow; the meadow line here follows
// --hero-ground-h, so the front row keeps the lab's distance to the meadow,
// the back row moves only a third as much, and the middle rows keep their
// lab proportions in between.
function rowBases(meadowLine) {
  const lab = LAB_ROWS.map((o) => o.at * LAB);
  const front = meadowLine - (LAB_MEADOW - LAB_ROWS[3].at) * LAB;
  const back = lab[0] - (LAB_MEADOW * LAB - meadowLine) * 0.35;
  return lab.map((y) => back + ((front - back) * (y - lab[0])) / (lab[3] - lab[0]));
}

// y (0 = top of the band, 100 = bottom) of every ground line as a function
// of x. meadowLine is where the meadow's top edge sits (from --hero-ground-h).
export function createTerrain(meadowLine) {
  const bases = rowBases(meadowLine);
  return {
    meadowLine,
    bases,
    farBack: (x) => peaks(x, FAR_BACK),
    farFront: (x) => peaks(x, FAR_FRONT),
    // The lab's gently undulating row grounds.
    rows: ROWS.map((row, i) => (x) => bases[i] - row.amp *
      (0.6 * Math.sin(x / row.freq + row.phase) + 0.4 * Math.sin(x / (row.freq * 0.43) + row.phase * 2))),
    meadowTop: (x) => meadowLine + (fbm(x / 34, 607, 2) - 0.5) * 3
  };
}

function sampleRidge(fn, x0, x1, step) {
  const pts = [];
  const count = Math.ceil((x1 - x0) / step);
  for (let i = 0; i <= count; i++) {
    const x = x0 + ((x1 - x0) * i) / count;
    pts.push({ x, y: fn(x) });
  }
  return pts;
}

// Catmull-Rom through the samples as cubic Béziers: smooth, organic ridges.
// The same samples always give the same curve, so facets drawn over a part
// of a ridge match its outline exactly.
function splineSegs(pts, from, to) {
  let d = '';
  const last = pts.length - 1;
  for (let i = from; i < to; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, last)];
    d += `C${n(p1.x + (p2.x - p0.x) / 6)},${n(p1.y + (p2.y - p0.y) / 6)} ` +
      `${n(p2.x - (p3.x - p1.x) / 6)},${n(p2.y - (p3.y - p1.y) / 6)} ${n(p2.x)},${n(p2.y)}`;
  }
  return d;
}

function ridgeBody(pts, bottom) {
  const a = pts[0];
  const z = pts[pts.length - 1];
  return `M${n(a.x)},${bottom}L${n(a.x)},${n(a.y)}` + splineSegs(pts, 0, pts.length - 1) +
    `L${n(z.x)},${bottom}Z`;
}

// Light comes from the upper right (sun by day, moon by night): the left
// flank of every summit is in shade. Each facet follows the ridge from the
// valley up to the summit and comes back down along a softly curved crease.
// The summits of a sampled ridge (indices into pts), each with the valley
// to its left; bumps less prominent than minProminence are ignored.
function summits(pts, minProminence) {
  const list = [];
  let prev = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    if (!(pts[i].y < pts[i - 1].y && pts[i].y <= pts[i + 1].y)) continue;
    let v = prev;
    for (let j = prev; j < i; j++) {
      if (pts[j].y > pts[v].y) v = j;
    }
    if (pts[v].y - pts[i].y < minProminence || i - v < 2) continue;
    prev = i;
    list.push({ i, v });
  }
  return list;
}

function facets(pts, bottom, minProminence, seed) {
  let d = '';
  summits(pts, minProminence).forEach((s) => {
    const S = pts[s.i];
    const V = pts[s.v];
    const dx = S.x - V.x;
    const depth = bottom - S.y;
    const bx = S.x - dx * (0.14 + hash(Math.round(S.x * 10), seed) * 0.2);
    d += `M${n(V.x)},${n(V.y)}` + splineSegs(pts, s.v, s.i) +
      `C${n(S.x - dx * 0.03)},${n(S.y + depth * 0.3)} ${n(bx + dx * 0.12)},${n(bottom - depth * 0.42)} ${n(bx)},${bottom}` +
      `L${n(V.x)},${n(bottom)}Q${n(V.x + dx * 0.06)},${n((V.y + bottom) / 2)} ${n(V.x)},${n(V.y)}Z`;
  });
  return d;
}

// A thin rim of light along the lit (right) slope just below each summit,
// where the sun / moon grazes the ridge. Stroked, not filled.
function rims(pts, minProminence) {
  let d = '';
  summits(pts, minProminence).forEach((s) => {
    let valley = s.i;
    while (valley < pts.length - 1 && pts[valley + 1].y >= pts[valley].y) valley++;
    const end = Math.min(s.i + Math.max(1, Math.round((valley - s.i) * 0.45)), pts.length - 1);
    const start = Math.max(s.i - 1, 0);
    d += `M${n(pts[start].x)},${n(pts[start].y)}` + splineSegs(pts, start, end);
  });
  return d;
}

function circle(cx, cy, r) {
  return `M${n(cx - r)},${n(cy)}a${n(r)},${n(r)} 0 1,0 ${n(2 * r)},0a${n(r)},${n(r)} 0 1,0 ${n(-2 * r)},0`;
}

function blade(x, yb, h, lean, bw) {
  return `M${n(x - bw / 2)},${n(yb)}Q${n(x - bw * 0.15 + lean * 0.35)},${n(yb - h * 0.6)} ${n(x + lean)},${n(yb - h)}` +
    `Q${n(x + bw * 0.2 + lean * 0.4)},${n(yb - h * 0.5)} ${n(x + bw / 2)},${n(yb)}Z`;
}

function gradient(id, y1, y2, stops) {
  return `<linearGradient id="${id}" gradientUnits="userSpaceOnUse" x1="0" y1="${n(y1)}" x2="0" y2="${n(y2)}">` +
    stops.map((s) => `<stop offset="${s[0]}" stop-color="${s[1]}" stop-opacity="${s[2]}"/>`).join('') +
    '</linearGradient>';
}

// A vertical gradient across each filled shape's own box (top to bottom),
// as the lab paints every tree and every mist band.
function boxGradient(id, stops) {
  return `<linearGradient id="${id}" x1="0" y1="0" x2="0" y2="1">` +
    stops.map((s) => `<stop offset="${s[0]}" stop-color="${s[1]}" stop-opacity="${s[2]}"/>`).join('') +
    '</linearGradient>';
}

function band(cls, x0, x1, y1, y2, id) {
  return `<rect class="${cls}" x="${n(x0)}" y="${n(y1)}" width="${n(x1 - x0)}" height="${n(y2 - y1)}" fill="url(#${id})"/>`;
}

// The warm light of sunset / sunrise on a layer. Two nested opacities (see
// .hl-dusk in HeroLandscape.vue) whose product is 0 in both steady themes,
// so it only glows while the theme changes, in step with the sky's dusk.
function dusk(id, d) {
  return `<g class="hl-dusk"><g class="hl-dusk-in"><path fill="url(#${id})" d="${d}"/></g></g>`;
}

/* ---------------------------------------------------------------------- */
/* Static layers (markup for each SVG)                                    */
/* ---------------------------------------------------------------------- */

// Far mountains: two hazy ranges with shade facets, dissolving into the
// horizon haze at their feet (atmospheric perspective).
export function farMarkup(T, x0, x1, ids) {
  const back = sampleRidge(T.farBack, x0, x1, 2);
  const front = sampleRidge(T.farFront, x0, x1, 2);
  return '<defs>' +
    gradient(ids.hazeDay, 14, 64, [[0, '#eef6ec', 0], [0.55, '#eef6ec', 0.5], [1, '#f2f7e8', 0.9]]) +
    gradient(ids.hazeNight, 14, 64, [[0, '#17362f', 0], [0.55, '#17362f', 0.4], [1, '#17362f', 0.7]]) +
    gradient(ids.duskFar, 8, 76, [[0, '#ffb67c', 0.7], [0.45, '#ff9f86', 0.36], [1, '#f39a9a', 0]]) +
    '</defs>' +
    `<path class="hl-far-back" d="${ridgeBody(back, 101)}"/>` +
    `<path class="hl-far-back-sh" d="${facets(back, 101, 6, 11)}"/>` +
    `<path class="hl-rim hl-rim-far" d="${rims(back, 6)}"/>` +
    `<path class="hl-far-front" d="${ridgeBody(front, 101)}"/>` +
    `<path class="hl-far-front-sh" d="${facets(front, 101, 5, 13)}"/>` +
    `<path class="hl-rim hl-rim-front" d="${rims(front, 5)}"/>` +
    band('hl-day', x0, x1, 14, 101, ids.hazeDay) +
    band('hl-night', x0, x1, 14, 101, ids.hazeNight) +
    dusk(ids.duskFar, ridgeBody(back, 101) + ridgeBody(front, 101));
}

// The lab's mist between the far mountains and the forest: it deepens down
// to the back row's ground, so the forest stands in a soft haze. Day and
// night versions crossfade with the sky, and the sunset glows in it.
export function mistMarkup(T, x0, x1, ids) {
  const y1 = T.bases[0] + LAB_MIST[0] * LAB;
  const y2 = T.bases[0] + LAB_MIST[1] * LAB;
  const day = FOREST_TONES.day;
  const night = FOREST_TONES.night;
  const rect = `M${n(x0)},${n(y1)}H${n(x1)}V${n(y2)}H${n(x0)}Z`;
  return '<defs>' +
    gradient(ids.mistDay, y1, y2, [[0, day.haze, 0], [1, day.haze, day.rows[0].haze * LAB_MIST_SHARE]]) +
    gradient(ids.mistNight, y1, y2, [[0, night.haze, 0], [1, night.haze, night.rows[0].haze * LAB_MIST_SHARE]]) +
    gradient(ids.duskMid, y1, y2, [[0, '#ffb07e', 0], [0.5, '#ffb07e', 0.3], [1, '#ff9f8a', 0]]) +
    '</defs>' +
    band('hl-day', x0, x1, y1, y2, ids.mistDay) +
    band('hl-night', x0, x1, y1, y2, ids.mistNight) +
    dusk(ids.duskMid, rect);
}

// A row's ground: everything below its ground line, which hides the feet of
// the row's trees (and the rows behind).
function groundPath(T, i, x0, x1) {
  return ridgeBody(sampleRidge(T.rows[i], x0, x1, 2.2), 101);
}

// The lab's mist on the feet of a row: a band from 0.9 x the row's shortest
// tree above its ground down to just below it, filled with a gradient that
// deepens downwards across the band's own box.
function hazePath(T, i, x0, x1) {
  const G = T.rows[i];
  const depth = ROWS[i].hazeDepth;
  const foot = LAB_HAZE_FOOT * LAB;
  const count = Math.ceil((x1 - x0) / 2.2);
  let d = '';
  for (let j = 0; j <= count; j++) {
    const x = x0 + ((x1 - x0) * j) / count;
    d += (j ? 'L' : 'M') + n1(x) + ',' + n1(G(x) - depth);
  }
  for (let j = count; j >= 0; j--) {
    const x = x0 + ((x1 - x0) * j) / count;
    d += 'L' + n1(x) + ',' + n1(G(x) + foot);
  }
  return d + 'Z';
}

function hazeGradient(id, tone, i) {
  const t = FOREST_TONES[tone];
  return boxGradient(id, [[0, t.haze, 0], [1, t.haze, t.rows[i].haze]]);
}

// A row's ground and mist in a static scenery layer: the ground's colour is
// a theme token, the day and night mists crossfade with the sky.
export function groundMarkup(T, i, x0, x1, ids) {
  const haze = hazePath(T, i, x0, x1);
  return '<defs>' + hazeGradient(ids.haze[i].day, 'day', i) + hazeGradient(ids.haze[i].night, 'night', i) + '</defs>' +
    `<path class="hl-g${i}" d="${groundPath(T, i, x0, x1)}"/>` +
    `<path class="hl-day" fill="url(#${ids.haze[i].day})" d="${haze}"/>` +
    `<path class="hl-night" fill="url(#${ids.haze[i].night})" d="${haze}"/>`;
}

// In a layer of living trees everything takes the theme from the layer's
// tone class or, for gradient fills, from an attribute switched by
// HeroLandscape.vue (.hl-toned: data-day / data-night hold the two fills),
// so a dusk snapshot of the layer keeps the colours it was taken in. Both
// tones' gradients live in the layer's defs, with fixed colours.
function tonedFill(ref, tone) {
  return `data-day="url(#${ref.day})" data-night="url(#${ref.night})" fill="url(#${ref[tone]})"`;
}

export function forestDefs(ids, rows, hazes) {
  let s = '<defs>';
  ['day', 'night'].forEach((tone) => {
    rows.forEach((i) => {
      const c = FOREST_TONES[tone].rows[i];
      s += boxGradient(ids.tree[i][tone], [[0, c.top, 1], [1, c.bottom, 1]]);
    });
    hazes.forEach((i) => {
      s += hazeGradient(ids.haze[i][tone], tone, i);
    });
  });
  return s + '</defs>';
}

// A row of trees, left to right, in one group whose fill (the row's
// gradient) every tree's body inherits.
export function rowMarkup(i, trees, ids, tone) {
  let s = `<g class="hl-row hl-row-${i} hl-toned" ${tonedFill(ids.tree[i], tone)}>`;
  for (let j = 0; j < trees.length; j++) s += treeMarkup(trees[j]);
  return s + '</g>';
}

// A row's ground and mist inside a layer of living trees (it has to cover
// the feet of the row's trees in the same layer).
export function liveGroundMarkup(T, i, x0, x1, ids, tone) {
  let s = `<path class="hl-g${i}" d="${groundPath(T, i, x0, x1)}"/>`;
  if (FOREST_TONES.day.rows[i].haze > 0 || FOREST_TONES.night.rows[i].haze > 0) {
    s += `<path class="hl-haze hl-toned" ${tonedFill(ids.haze[i], tone)} d="${hazePath(T, i, x0, x1)}"/>`;
  }
  return s;
}

// The meadow the rabbit hops on: a lighter ground surface with grass blades
// along its edge, a little texture, sparse tiny flowers by day and a
// moonlit sheen along its edge by night.
export function meadowMarkup(T, x0, x1, ids) {
  const top = sampleRidge(T.meadowTop, x0, x1, 4);
  const mt = T.meadowLine;
  const depth = 100 - mt;
  let dark = '';
  let light = '';
  let k0 = Math.floor(x0 / 0.9) - 1;
  let k1 = Math.ceil(x1 / 0.9) + 1;
  for (let k = k0; k <= k1; k++) {
    const x = (k + 0.5 + (hash(k, 911) - 0.5) * 0.8) * 0.9;
    const clump = 0.6 + 0.6 * noise(x / 6, 913);
    const h = (0.9 + hash(k, 915) * 1.9) * clump;
    const yb = T.meadowTop(x) + 0.6 + hash(k, 917) * 0.9;
    const d = blade(x, yb, h, (hash(k, 919) - 0.4) * 1.3, 0.42 + hash(k, 921) * 0.2);
    if (hash(k, 923) < 0.5) dark += d; else light += d;
  }
  // Short blades scattered over the meadow, bigger closer to the viewer.
  k0 = Math.floor(x0 / 2.1) - 1;
  k1 = Math.ceil(x1 / 2.1) + 1;
  for (let k = k0; k <= k1; k++) {
    if (hash(k, 931) > 0.45) continue;
    const x = (k + hash(k, 933)) * 2.1;
    const f = hash(k, 935);
    const yb = mt + 3 + f * (depth - 4);
    const h = (0.7 + hash(k, 937) * 1.1) * (0.8 + 0.6 * f);
    const d = blade(x, yb, h, (hash(k, 939) - 0.4) * 0.9, 0.38 + 0.16 * f);
    if (hash(k, 941) < 0.55) dark += d; else light += d;
  }
  // Tiny five-petal flowers, sparse.
  const petals = { w: '', y: '', p: '' };
  let centres = '';
  k0 = Math.floor(x0 / 9) - 1;
  k1 = Math.ceil(x1 / 9) + 1;
  for (let k = k0; k <= k1; k++) {
    if (hash(k, 951) > 0.5) continue;
    const x = (k + hash(k, 953)) * 9;
    const f = hash(k, 955);
    const y = mt + 2.6 + f * (depth - 5);
    const s = 0.8 + 0.45 * f;
    const roll = hash(k, 957);
    const key = roll < 0.45 ? 'w' : (roll < 0.8 ? 'y' : 'p');
    const rot = hash(k, 959) * 6.28;
    for (let j = 0; j < 5; j++) {
      const a = rot + (j * Math.PI * 2) / 5;
      petals[key] += circle(x + Math.cos(a) * 0.5 * s, y + Math.sin(a) * 0.5 * s, 0.4 * s);
    }
    centres += circle(x, y, 0.28 * s);
  }
  const body = ridgeBody(top, 101);
  return '<defs>' +
    gradient(ids.meadowSheen, mt - 1.5, 101, [[0, '#fff', 0.2], [0.13, '#fff', 0], [0.55, '#000', 0], [1, '#000', 0.13]]) +
    gradient(ids.meadowSheenNight, mt - 1.5, 101, [[0, '#cdeedc', 0.2], [0.12, '#cdeedc', 0.03], [0.5, '#000', 0], [1, '#000', 0.2]]) +
    '</defs>' +
    `<path class="hl-md" d="${body}"/>` +
    `<path class="hl-day" fill="url(#${ids.meadowSheen})" d="${body}"/>` +
    `<path class="hl-night" fill="url(#${ids.meadowSheenNight})" d="${body}"/>` +
    `<path class="hl-bl-d" d="${dark}"/>` +
    `<path class="hl-bl-l" d="${light}"/>` +
    '<g class="hl-day">' +
    `<path class="hl-fl-w" d="${petals.w}"/><path class="hl-fl-y" d="${petals.y}"/>` +
    `<path class="hl-fl-p" d="${petals.p}"/><path class="hl-fl-c" d="${centres}"/>` +
    '</g>';
}

/* ---------------------------------------------------------------------- */
/* Trees (the lab's spire)                                                */
/* ---------------------------------------------------------------------- */

// A slender conifer spire with soft, slightly irregular steps along its
// edges (branch whorls seen from afar) and a fine leader. Returns the
// outline and a thin lit sliver along the right edge. Local coordinates:
// feet at (0,0), tip at (0,-h). fmt rounds the numbers.
// The lab also gives the two nearest rows a short trunk under the skirt, but
// they sink their trees' feet deeper into the ground than that trunk reaches
// up (at most 0.03 h above the feet, against a sink of 0.125 h or more), so
// it is never seen and is left out: one path less for each near tree.
export function spire(h, o, r, fmt) {
  const W = h * o.width;
  const base = -h * o.skirt;
  const steps = o.steps;
  const sideR = [];
  const sideL = [];
  const prof = (t) => Math.pow(1 - t, o.taper);
  for (let side = 0; side < 2; side++) {
    const out = side === 0 ? sideR : sideL;
    const off = side === 0 ? 0 : 0.5;
    for (let i = 0; i < steps; i++) {
      const t = (i + off) / steps;
      if (t > 0.94) continue;
      const tn = Math.min((i + off + 0.62) / steps, 0.97);
      const y = base + (-h - base) * t;
      const yn = base + (-h - base) * tn;
      const w = W * prof(t) * (0.9 + r() * 0.2);
      const wn = W * prof(tn) * (1 - o.notch * (0.8 + r() * 0.4));
      out.push([w, y + h * o.droop], [wn, yn]);
    }
  }
  const pts = [[0, -h]];
  for (let i = sideR.length - 1; i >= 0; i--) pts.push([sideR[i][0], sideR[i][1]]);
  pts.push([W * 0.3, base + h * 0.01], [-W * 0.3, base + h * 0.01]);
  for (let i = 0; i < sideL.length; i++) pts.push([-sideL[i][0], sideL[i][1]]);
  // Soft corners.
  const k = o.soft;
  let d = '';
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const a = pts[(i - 1 + pts.length) % pts.length];
    const b = pts[(i + 1) % pts.length];
    const pa = [p[0] + (a[0] - p[0]) * 0.5 * k, p[1] + (a[1] - p[1]) * 0.5 * k];
    const pb = [p[0] + (b[0] - p[0]) * 0.5 * k, p[1] + (b[1] - p[1]) * 0.5 * k];
    d += (i ? 'L' : 'M') + fmt(pa[0]) + ',' + fmt(pa[1]) + 'Q' + fmt(p[0]) + ',' + fmt(p[1]) + ' ' + fmt(pb[0]) + ',' + fmt(pb[1]);
  }
  d += 'Z';
  // Lit sliver: the right edge, closed by an inner line at ~55% of the width.
  let lit = `M0,${fmt(-h)}`;
  for (let i = sideR.length - 1; i >= 0; i--) lit += `L${fmt(sideR[i][0])},${fmt(sideR[i][1])}`;
  lit += `L${fmt(W * 0.3)},${fmt(base + h * 0.01)}`;
  for (let i = 0; i < sideR.length; i++) {
    const p = sideR[i];
    lit += `L${fmt(p[0] * o.litIn)},${fmt(p[1] - h * 0.01)}`;
  }
  lit += 'Z';
  return { d, lit };
}

// Three proportions: spruce, fir and a slim spire.
export const KINDS = {
  spruce: { width: 0.23, skirt: 0.06, steps: 6, taper: 1.0, droop: 0.014, notch: 0.24, soft: 0.65, litIn: 0.42 },
  fir: { width: 0.29, skirt: 0.07, steps: 5, taper: 0.9, droop: 0.018, notch: 0.22, soft: 0.75, litIn: 0.38 },
  slim: { width: 0.17, skirt: 0.05, steps: 7, taper: 1.1, droop: 0.012, notch: 0.22, soft: 0.6, litIn: 0.46 }
};

// Species are mixed naturally: half spruces, then firs, a few slim spires.
function pickKind(r) {
  const v = r();
  return v < 0.5 ? 'spruce' : (v < 0.8 ? 'fir' : 'slim');
}

// The spire is proportional to its height, so the forest draws on a small
// library of shapes, SHAPE_VARIANTS per kind, made at SHAPE_H units tall,
// and each tree scales its own to its height (with the transform that
// places, grows and sways it): hundreds of trees, a few dozen shapes to
// compute — and their steps are irregular enough that no repeat shows.
export const SHAPE_H = 10;
const SHAPE_VARIANTS = 16;
const shapes = {};

// One tree's markup, its feet at 0,0, SHAPE_H tall (HeroLandscape.vue
// places, scales, grows and sways it with one transform). The body takes
// its row's gradient from the row's group; the two nearest rows add the
// lit sliver.
export function treeMarkup(tree) {
  const near = ROWS[tree.row].near;
  const variant = tree.seed % SHAPE_VARIANTS;
  const key = tree.kind + variant + (near ? 'n' : '');
  if (!shapes[key]) {
    const kinds = Object.keys(KINDS);
    const r = seededRandom(9001 + kinds.indexOf(tree.kind) * 101 + variant);
    const s = spire(SHAPE_H, KINDS[tree.kind], r, n);
    shapes[key] = near
      ? `<g display="none"><path d="${s.d}"/><path class="hl-lt" d="${s.lit}"/></g>`
      : `<path display="none" d="${s.d}"/>`;
  }
  return shapes[key];
}

/* ---------------------------------------------------------------------- */
/* The forest: where each tree stands, and when it grows                  */
/* ---------------------------------------------------------------------- */

// s: the entrance wave grows from WAVE_FROM (in the centre) and is over by
// WAVE_END (at the edges).
export const WAVE_FROM = 0.9;
export const WAVE_END = 3.5;
// After the entrance the empty spots fill in lanes, one tree after another
// per lane, a breath apart; the lanes start a few seconds apart, so 1-3
// trees grow at once.
const FILL_FROM = WAVE_END - 0.4;
const LANE_STAGGER = 4.6;
// The most spots one lane fills, so that a visitor sees the forest full
// within ~7 minutes (10-16 s a tree) on any screen: the entrance leaves
// empty only that many of the spots that can start empty, the ones that
// show best (see planGrowth), and grows the others.
const FILL_PER_LANE = 30;
const GOLDEN = 0.6180339887;

function frac(v) {
  return v - Math.floor(v);
}

// One side of a row, walking out from the centre as the lab walks along its
// rows: each tree a random step after the previous one (and, in the front
// row, a wider gap after every cluster of 2-5 trees). The walk always starts
// from the same place with the same random stream, so the trees near the
// centre stay put whatever the width; a wider screen only walks further.
function walk(row, dir, limit, out) {
  const r = seededRandom(row.salt * 2 + (dir > 0 ? 1 : 0));
  let left = 2 + Math.floor(r() * 4);
  // The cluster being walked: its size and the next tree's place in it.
  let size = left - 1;
  let pos = 0;
  let x = dir > 0 ? r() * row.gapMin : -(row.gapMin + r() * row.gapRange);
  let k = dir > 0 ? 0 : -1;
  while (dir > 0 ? x <= limit : x >= -limit) {
    if (row.clusters && --left <= 0) {
      x += dir * (LAB_CLUSTER_GAP[0] + r() * LAB_CLUSTER_GAP[1]) * LAB;
      left = 2 + Math.floor(r() * 4);
      size = left;
      pos = 0;
      if (dir > 0 ? x > limit : x < -limit) break;
    }
    const h = row.hMin + r() * row.hRange;
    const kind = pickKind(r);
    const seed = Math.floor(r() * 1e6);
    // In the clustered row the spots that can start empty are at the edges
    // of the clusters (the outer edge of a cluster of 3-5 and of every
    // other pair, the inner one too of a cluster of 5: about a third in
    // all), so the clusters stand compact from the start and spread as they
    // fill.
    const outer = pos === size - 1 && (size >= 3 || (size === 2 && hash(k, row.salt + 10) < 0.5));
    const edge = outer || (pos === 0 && size >= 5);
    out.push({ k, x, h, kind, seed, edge });
    x += dir * (row.gapMin + r() * row.gapRange);
    k += dir;
    pos++;
  }
}

// Every tree of every row within -limit..limit, each row sorted left to
// right (the lab's drawing order: a tree covers its left neighbour).
// halfView is the visible half width, used to spread the entrance wave
// from the centre to the edges.
export function makeForest(T, limit, halfView) {
  return ROWS.map((row, i) => {
    const spots = [];
    walk(row, -1, limit, spots);
    spots.reverse();
    walk(row, 1, limit, spots);
    const G = T.rows[i];
    return spots.map((s) => {
      const k = s.k;
      const wave = Math.pow(Math.min(1, Math.abs(s.x) / Math.max(halfView, 1)), 0.9);
      const waveStart = WAVE_FROM + row.delay + 0.9 * wave + hash(k, row.salt + 3) * 0.12;
      const waveDur = 1.2 + 0.3 * hash(k, row.salt + 4);
      return {
        key: i * 100000 + k,
        row: i,
        k,
        x: s.x,
        ground: G(s.x),
        h: s.h,
        // The tree's shape is SHAPE_H tall (see treeMarkup).
        scale: s.h / SHAPE_H,
        kind: s.kind,
        seed: s.seed,
        sink: row.sink,
        // A golden-ratio sequence along the row spreads the spots that can
        // start empty evenly through the forest, never in clumps (in the
        // clustered row they are the edges of its clusters, see walk).
        free: row.clusters ? s.edge : frac(k * GOLDEN + row.salt * 0.1371) < row.free,
        waveStart,
        waveDur,
        growDur: lerp(row.grow[0], row.grow[1], hash(k, row.salt + 5)),
        rest: 0.8 + 1.7 * hash(k, row.salt + 6),
        start: waveStart,
        dur: waveDur,
        planned: false,
        // The two nearest rows sway, very gently, from the base: a slow
        // breeze of two harmonics (degrees, s, radians).
        sways: row.near,
        amp: 0.2 + 0.14 * hash(k, row.salt + 7),
        period: 4.6 + 3.2 * hash(k, row.salt + 8),
        phase: hash(k, row.salt + 9) * Math.PI * 2
      };
    });
  });
}

// Plans when each empty spot fills.
// Which spots the entrance leaves empty: of the spots in view that can
// start empty, at most FILL_PER_LANE per lane, the ones that show best —
// the front row's cluster edges first, then the others from the centre
// outwards with a random spread, so the gaps (up to about a third of the
// trees in the middle of the scene) thin out towards the sides. Every other
// spot grows with the entrance, those out of view too.
// When: the empty spots in an order that hops around the scene (the first
// ones near the centre and in the front rows, then spreading out to the
// edges and the middle row), shared between `lanes` lanes.
// The scene is rebuilt at time `now` with the earlier plan: once a tree can
// have shown, its spot keeps what that plan gave it (grown with the
// entrance, growing or grown), so no tree ever disappears, and no new gap
// opens; the spots still waiting are planned again from `now` on.
// Returns the plan to hand to the next call.
export function planGrowth(trees, halfView, lanes, now, previous) {
  // Before the entrance shows its first tree there is nothing to keep.
  const before = now < WAVE_FROM ? null : previous;
  const free = [];
  for (let j = 0; j < lanes; j++) free.push(Math.max(FILL_FROM + j * LANE_STAGGER, now + 0.5));
  const earliest = () => {
    let j = 0;
    for (let i = 1; i < free.length; i++) {
      if (free[i] < free[j]) j = i;
    }
    return j;
  };
  // Spots already left empty, still waiting for their tree, and spots never
  // planned yet.
  const waiting = [];
  const fresh = [];
  trees.forEach((tree) => {
    tree.start = tree.waveStart;
    tree.dur = tree.waveDur;
    tree.planned = false;
    if (!tree.free) return;
    const old = before ? before[tree.key] : null;
    if (old && (!old.planned || old.start <= now)) {
      tree.start = old.start;
      tree.dur = old.dur;
      tree.planned = old.planned;
      if (old.planned && old.start + old.dur > now) {
        // Still growing: its lane is busy until it is done.
        const j = earliest();
        free[j] = Math.max(free[j], old.start + old.dur + tree.rest);
      }
    } else if (Math.abs(tree.x) <= halfView + 6) {
      (old ? waiting : fresh).push(tree);
    }
  });
  let pending = waiting;
  if (!before) {
    fresh.forEach((tree) => {
      const row = ROWS[tree.row];
      const centre = Math.min(1, Math.abs(tree.x) / Math.max(halfView, 1));
      tree.pick = centre + 0.8 * hash(tree.k, row.salt + 11) - (row.clusters ? 0.4 : 0);
    });
    fresh.sort((a, b) => a.pick - b.pick);
    pending = fresh.slice(0, lanes * FILL_PER_LANE);
  }
  pending.sort((a, b) => a.x - b.x);
  pending.forEach((tree, i) => {
    const centre = Math.min(1, Math.abs(tree.x) / Math.max(halfView, 1));
    tree.order = frac(i * GOLDEN + 0.31) * 0.55 + centre * 0.45 + (3 - tree.row) * 0.18;
  });
  pending.sort((a, b) => a.order - b.order);
  pending.forEach((tree) => {
    const j = earliest();
    tree.start = free[j];
    tree.dur = tree.growDur;
    tree.planned = true;
    free[j] = tree.start + tree.dur + tree.rest;
  });
  const plan = {};
  trees.forEach((tree) => {
    if (tree.free) plan[tree.key] = { start: tree.start, dur: tree.dur, planned: tree.planned };
  });
  return plan;
}

// How far a tree has grown at time t: 0 = nothing yet, 1 = grown.
export function growthAt(tree, t) {
  if (t <= tree.start) return 0;
  return Math.min(1, (t - tree.start) / tree.dur);
}

// The lab's growth: the height rises from 4% to full with a smooth
// ease-in-out, and the width follows a little ahead (sx = sy^0.85), so a
// sapling is slender and fills out as it grows. No overshoot.
export function growScale(p) {
  if (p >= 1) return 1;
  return 0.04 + 0.96 * ease.inOutCubic(Math.max(p, 0));
}
